import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	CommonButtonRowWidget,
	CommonDateTimeFieldWidget,
	CommonSelectFieldWidget,
	CommonTextFieldWidget
} from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, EngagementActivity, EngagementExternalRoleId, StaffData, StaffRoleId } from '@interfaces';
import { AppService } from '../../../../services';
import { PersonFinderWidget } from '../../../widget/search';
import { PersonDialogDataService } from '../../person-dialog';
import { StaffSelectorWidget } from '../../staff-selector';
import { FollowUpDialogService } from '../follow-up';
import { EngagementEditorForm } from './engagement-editor.form';
import { EngagementEditorDialogService } from './engagement-editor.service';

export type EngagementEditorDialogData = | { mode: DialogMode.ADD; staffRoleId?: StaffRoleId, followUpId?: number, externalRoleId?: EngagementExternalRoleId, personId?: number } | { mode: DialogMode.EDIT; engagementId: number; };

@Component({
	selector: 'programs-engagement-editor-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		CommonTextFieldWidget,
		CommonSelectFieldWidget,
		CommonDateTimeFieldWidget,
		FormsModule,
		StaffSelectorWidget,
		PersonFinderWidget,
		CommonButtonRowWidget
	],
	templateUrl: './engagement-editor.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	"styles": [
		`
			.white-box {
				background-color: #eeeeee;
				padding: 1em;
				margin-bottom: 0.5em;
			}
		`
	]
})

export class EngagementEditorDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<EngagementEditorDialog>);
	public dialogContext: DialogContext<EngagementEditorDialogData> = inject(MAT_DIALOG_DATA);

	private readonly engagementEditorDialogService = inject(EngagementEditorDialogService);
	private readonly dialogService = inject(DialogService);
	public readonly app = inject(AppService);
	private readonly followUpService = inject(FollowUpDialogService);
	private readonly personDialogDataService = inject(PersonDialogDataService);

	public hasPersonFieldError: boolean = false;
	public searchPersonDisabled = signal<boolean>(false);
	public readonly showEngagementForm = signal<boolean>(false);

	public readonly form = new EngagementEditorForm();

	public selectedPersonName: string | undefined;
	public staffData = signal<StaffData[]>([]);
	public values = this.form.values;

	public readonly headerTitle = computed(() => {
		if (this.dialogContext.data?.mode == DialogMode.ADD) return 'Add Engagement';
		return 'Edit Engagement';
	});
	public readonly buttonText = computed(() => {
		return (this.dialogContext.data?.mode == DialogMode.EDIT) ? 'Update' : 'Add';
	});

	@ViewChild('addBtn')
	set formSubmitButton(button: ElementRef<HTMLButtonElement>) {
		if (button) {
			this.form.commonForm.setSubmitButton(button, async (values, changed: string[]) => {

				await this.saveEngagement(false);
			});
		}
	}

	public saveAndCloseButtonDisabled = computed(() => {
		const activity = this.form.activity();
		const activityValue = activity.pendingValue() ?? activity.actualValue();
		return activityValue !== EngagementActivity.Engaged;
	});

	setSelectedPersonId(id: number) {
		const values = this.values();
		this.values.set({ ...values, personId: id });
	}

	handleStaffValueChange(value: { staffId?: number } | undefined) {
		const values = this.values();
		this.values.set({ ...values, contactInteraction: { ...values.contactInteraction, staffId: value?.staffId } });
	};

	async ngOnInit() {
		this.selectedPersonName = undefined;
		this.form.resetForm();

		const data = this.dialogContext.data;
		if (!data) return;

		if (data.mode === DialogMode.ADD && data.staffRoleId)
			this.values.set({ ...this.values(), staffRoleId: data.staffRoleId });

		if (data.mode === DialogMode.ADD && data.externalRoleId)
			this.values.set({ ...this.values(), externalRoleId: data.externalRoleId });

		this.addInteraction();
		this.staffData.set(this.app.data().staff);

		if (data.mode === DialogMode.EDIT) {
			const newValues = await this.engagementEditorDialogService.getEngagementById(data.engagementId);
			if (newValues) {
				this.values.set({
					personId: newValues.person.id,
					engagementDateUTC: newValues.engagementDateUTC,
					externalRoleId: newValues.externalRoleId,
					purposeId: newValues.purposeId,
					activity: newValues.activity,
					staffRoleId: newValues.staffRoleId,
					details: newValues.details,
					isDonorPotential: !!newValues.donorPotentialDetails,
					donorPotentialDetails: newValues.donorPotentialDetails ?? '',
					contactInteraction: newValues.contactInteractions.sort((a, b) => a.engagementContactId - b.engagementContactId)[0] ?? this.form.defaultContactInteractionValues()
				});
				this.selectedPersonName = newValues.person.fullName;
				this.searchPersonDisabled.set(true);
			}
		} else if (data.followUpId) {
			const result = await this.followUpService.getFollowUpById(data.followUpId);
			if (result) {
				const oldValues = { ...this.values() };
				oldValues.contactInteraction.staffId = result.staffId;
				this.values.set({
					...oldValues,
					personId: result.personId,
					purposeId: result.purposeId,
					externalRoleId: result.externalRoleId,
					staffRoleId: result.staffRoleId,
					engagementDateUTC: result.dueByUTC!,
				})
				this.selectedPersonName = result.personName;
				this.searchPersonDisabled.set(true);
			}
		} else if (data.personId) {
			await this.personDialogDataService.getPersonDetails(data.personId, false);
			const personDetails = this.personDialogDataService.personDetails();
			if (personDetails) {
				const oldValues = { ...this.values() };
				oldValues.personId = personDetails.personId;
				this.values.set({
					...oldValues,
					externalRoleId: data.externalRoleId ?? undefined,
					staffRoleId: data.staffRoleId ?? undefined,
				});
				this.selectedPersonName = personDetails.fullName;
				this.searchPersonDisabled.set(true);
			}
		}

		this.showEngagementForm.set(true);
	}


	public onChangeDonorPotential(isChecked: boolean) {
		this.form.donorPotentialDetails().config.set({
			...this.form.donorPotentialDetails().config(),
			isRequired: isChecked
		});

		this.values.set({
			...this.values(),
			isDonorPotential: isChecked,
		});

		this.form.commonForm.resetAll();
	}

	public async closeDialog(recordId?: number) {
		// psuedo action to pass back the created/updated record id to the caller
		const action: DialogAction = {
			id: 'add',
			enabled: signal(true),
			visible: signal(true),
			text: signal('Add'),
			cssClass: signal('btn-standard'),
			linkType: "no-action",
			callbackResult: recordId,
			willCloseDialog: true,
		}
		this.dialogRef.close(action);
	}

	private addInteraction() {
		this.values.set({
			...this.values(),
			contactInteraction: this.form.defaultContactInteractionValues()
		});
	}

	private async saveEngagement(markAsClosed: boolean) {
		const valid = this.form.checkIfValid();

		const data = this.dialogContext.data;
		if (!data) return;

		if (!this.form.isDonorPotential()) this.values.set({ ...this.values(), donorPotentialDetails: '' });
		if (valid) {
			let id;
			if (data.mode === DialogMode.ADD) {
				id = await this.engagementEditorDialogService.addEngagementDetails({ ...this.values() }, data.followUpId);
			} else {
				id = await this.engagementEditorDialogService.updateEngagement(data.engagementId, { ...this.values() });
			}

			if (id && markAsClosed && this.values().activity === EngagementActivity.Engaged) {
				await this.engagementEditorDialogService.closeEngagement(id);
			}
			this.closeDialog(id);
		}
		else
			this.showInvalidFormMessage();
	}

	public async saveAndMarkClosed() {
		if (this.values().activity !== EngagementActivity.Engaged) return;

		await this.saveEngagement(true);
	}

	private showInvalidFormMessage() {
		this.hasPersonFieldError = !this.values().personId;
		this.form.commonForm.resetAll(true);
		this.dialogService.showMessage('Invalid Form', 'Invalid form please fill out all required fields.', 'Ok', [], 400, 200);
	}
}